import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import SchoolIcon from '@mui/icons-material/School'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { useTranslation } from 'react-i18next'
import { isPrerendering } from '../lib/prerender'
import { usePreferences } from '../preferences/PreferencesContext'
import { useAudioPlayer } from '../hooks/useAudioPlayer'
import { useAuth } from '../hooks/useAuth'
import { useBookmarks } from '../hooks/useBookmarks'
import { useMemorization } from '../hooks/useMemorization'
import { useReaderState } from '../hooks/useReaderState'
import { useFollowActiveVerse } from '../hooks/useFollowActiveVerse'
import { useDetachPlaylistOnReaderPlay } from '../audio/PlaylistAudioContext'
import { InlinePlayer } from '../components/InlinePlayer'
import { ReaderFloatingSettings } from '../components/ReaderFloatingSettings'
import { quranApi } from '../lib/quranApi'
import { progressApi } from '../lib/api'
import { progressCreditOnAyahChange } from '../lib/progressCredit'
import type { Ayah, Edition } from '../types'

interface SurahReaderChromeProps {
  surahNumber: number
  ayahs: Ayah[]
}

/**
 * Interactive chrome layer for the public, prerendered SurahReaderPage.
 *
 * This component is the SOLE owner of the interactive UI on the public
 * reader route — audio playback, the floating settings rail, and the
 * per-ayah action buttons (play / bookmark / memorize). It MUST stay
 * separate from the SEO-shaped body that SurahReaderPage renders so
 * crawlers see a lean snapshot.
 *
 * It mounts the same `useAudioPlayer` engine, `useBookmarks`,
 * `useMemorization`, and `usePreferences` that QuranReaderPage uses, so
 * real visitors get an equivalent (interactive) reader experience.
 *
 * Four named chrome wrappers, each gated by `isPrerendering()`:
 *   - `AudioPlayerChrome` (bottom-floating `InlinePlayer`)
 *   - `ReaderSettingsChrome` (right-edge `ReaderFloatingSettings` rail)
 *   - `AyahActionsChrome` (per-ayah play/bookmark/memorize buttons,
 *     portalled into each `<article id="ayah-N">`)
 *   - `ContinueInReaderChrome` (top-floating "Continue in the full
 *     reader" CTA, navigating to `/reader/<n>`) — the client-only escape
 *     hatch from this SEO page into the full interactive Reader; never
 *     rendered into the prerendered markup, so it carries no crawlable
 *     link to a robots.txt-disallowed route (see SurahIndexList.tsx for
 *     the equivalent rule on the crawl-hub side)
 */
export function SurahReaderChrome({ surahNumber, ayahs }: SurahReaderChromeProps) {
  if (isPrerendering()) return null
  return <SurahReaderChromeInner surahNumber={surahNumber} ayahs={ayahs} />
}

/**
 * Inner shell — only mounted for real visitors. Owns ALL hook subscriptions
 * so the prerender path never even loads the audio engine, bookmark store,
 * or memorization list.
 */
function SurahReaderChromeInner({ surahNumber, ayahs }: SurahReaderChromeProps) {
  const { prefs, setPref } = usePreferences()
  const { isAuthenticated } = useAuth()
  const { recordPosition, recordAudioPosition } = useReaderState()

  const reciter = prefs.reciter
  const edition = prefs.edition
  const arabicFont = prefs.arabicFont
  const fontSize = prefs.fontSize
  const wbwEnabled = prefs.wbwEnabled
  const viewMode = prefs.viewMode

  // Editions list for the settings rail. Best-effort; failures leave the
  // list empty (the rail handles that gracefully — see ReaderFloatingSettings).
  const [availableEditions, setAvailableEditions] = useState<Edition[]>([])
  useEffect(() => {
    let cancelled = false
    quranApi
      .listEditions()
      .then((list) => {
        if (!cancelled) setAvailableEditions(list)
      })
      .catch(() => {
        /* best-effort */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const lastAyahDurationRef = useRef(0)
  const progressVerseRef = useRef<{ surahNumber: number; ayahNumber: number } | null>(null)

  // Audio engine. Reader follows natural surah order — `fetchNextSurah`
  // returns null past 114 so the engine stops cleanly at the end of the
  // Quran (no auto-loop). For now we don't hand off across surahs from
  // this public reader (the SEO page is single-surah). Surah hand-off
  // wiring stays minimal — a future task can hook it into navigate().
  const audioPlayer = useAudioPlayer({
    ayahs,
    reciterEdition: reciter,
    fetchNextSurah: async (n) => {
      if (n < 1 || n > 114) return null
      try {
        const data = await quranApi.getSurahWithTranslation(n, edition)
        return { ayahs: data.ayahs, surahData: data }
      } catch {
        return null
      }
    },
    onTimeTick: (ayahIndex, currentTime) => {
      const ayah = ayahs[ayahIndex]
      if (!ayah) return
      recordAudioPosition({
        surahNumber: ayah.surahNumber,
        ayahNumber: ayah.ayahNumber,
        currentTime,
        reciter,
      })
    },
    onAyahChange: (ayahIndex, item) => {
      const started =
        item?.surahNumber && item?.ayahNumber
          ? { surahNumber: item.surahNumber, ayahNumber: item.ayahNumber }
          : ayahs[ayahIndex]
            ? { surahNumber: ayahs[ayahIndex].surahNumber, ayahNumber: ayahs[ayahIndex].ayahNumber }
            : null
      if (isAuthenticated && started) {
        const credit = progressCreditOnAyahChange(
          progressVerseRef.current,
          started,
          Math.round(lastAyahDurationRef.current),
        )
        if (credit) {
          progressApi
            .record(credit.surahNumber, credit.ayahNumber, credit.durationSec)
            .catch(() => {
              /* best-effort */
            })
        }
      }
      if (started) progressVerseRef.current = started
    },
  })

  useEffect(() => {
    if (audioPlayer.duration > 0) {
      lastAyahDurationRef.current = audioPlayer.duration
    }
  }, [audioPlayer.duration])

  // Detach the global playlist player whenever the reader's engine commits
  // to a play — same pattern as QuranReaderPage. Without this the two
  // engines race for the speaker.
  useDetachPlaylistOnReaderPlay(audioPlayer.loading || audioPlayer.isPlaying)

  // Auto-scroll the active verse into view during playback.
  useFollowActiveVerse(audioPlayer.currentAyahIndex, ayahs.length)

  // Track reading position as audio progresses so the user can resume
  // where they left off on a later visit.
  useEffect(() => {
    const ayah = ayahs[audioPlayer.currentAyahIndex]
    if (!ayah) return
    recordPosition(surahNumber, ayah.ayahNumber)
  }, [audioPlayer.currentAyahIndex, ayahs, surahNumber, recordPosition])

  const { isBookmarked, toggleBookmark } = useBookmarks({ autoLoad: isAuthenticated })
  const { isAdded: isInMemorization, toggleVerse: toggleMemorization } = useMemorization({
    autoLoad: isAuthenticated,
  })

  const handleBookmarkToggle = useCallback(
    (ayahNumber: number) => {
      void toggleBookmark(surahNumber, ayahNumber)
    },
    [toggleBookmark, surahNumber],
  )

  const handleMemorizeToggle = useCallback(
    (ayahNumber: number) => {
      void toggleMemorization(surahNumber, ayahNumber)
    },
    [toggleMemorization, surahNumber],
  )

  const hasAudio =
    audioPlayer.isPlaying || audioPlayer.loading || audioPlayer.currentTime > 0

  return (
    <>
      <ContinueInReaderChrome surahNumber={surahNumber} />

      <ReaderSettingsChrome
        edition={edition}
        availableEditions={availableEditions}
        reciter={reciter}
        arabicFont={arabicFont}
        fontSize={fontSize}
        wbwEnabled={wbwEnabled}
        viewMode={viewMode}
        onEditionChange={(v) => setPref('edition', v)}
        onReciterChange={(v) => setPref('reciter', v)}
        onArabicFontChange={(v) => setPref('arabicFont', v)}
        onFontSizeChange={(v) => setPref('fontSize', v)}
        onWbwToggle={() => setPref('wbwEnabled', !wbwEnabled)}
        onViewModeChange={(v) => setPref('viewMode', v)}
      />

      {/* Per-ayah action buttons. Mounted as children of each
          <article id="ayah-N"> element via a portal-like
          appendChild side effect inside AyahActionsChrome. */}
      <AyahActionsChromeMount
        ayahs={ayahs}
        isAuthenticated={isAuthenticated}
        isBookmarked={(ayahNumber) => isBookmarked(surahNumber, ayahNumber)}
        isInMemorization={(ayahNumber) => isInMemorization(surahNumber, ayahNumber)}
        currentAyahIndex={audioPlayer.currentAyahIndex}
        isPlaying={audioPlayer.isPlaying}
        onPlay={(index) => {
          void audioPlayer.playAyah(index)
        }}
        onBookmarkToggle={handleBookmarkToggle}
        onMemorizeToggle={handleMemorizeToggle}
      />

      <AudioPlayerChrome
        active={ayahs.length > 0 && hasAudio}
        isPlaying={audioPlayer.isPlaying}
        loading={audioPlayer.loading}
        currentTime={audioPlayer.currentTime}
        duration={audioPlayer.duration}
        surahNumber={surahNumber}
        currentAyahNumber={audioPlayer.currentAyah?.ayahNumber ?? null}
        continuousMode={audioPlayer.continuousMode}
        onPlayPause={audioPlayer.togglePlay}
        onNext={audioPlayer.nextAyah}
        onPrevious={audioPlayer.previousAyah}
        onSeek={audioPlayer.seek}
        onSeekToAyah={audioPlayer.playAyah}
        onToggleContinuousMode={audioPlayer.toggleContinuousMode}
        volume={audioPlayer.volume}
        onVolumeChange={audioPlayer.setVolume}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Audio player chrome
// ---------------------------------------------------------------------------

interface AudioPlayerChromeProps {
  /** True once any audio interaction is in progress — drives the
   *  conditional mount of the `InlinePlayer` bar. The wrapper element
   *  (with `data-testid="audio-player-chrome"`) is always present for
   *  real visitors so chrome-gating tests don't need to wait for audio
   *  state to flip. */
  active: boolean
  isPlaying: boolean
  loading: boolean
  currentTime: number
  duration: number
  surahNumber: number
  currentAyahNumber: number | null
  continuousMode: boolean
  onPlayPause: () => void
  onNext: () => void
  onPrevious: () => void
  onSeek: (time: number) => void
  onSeekToAyah: (index: number) => Promise<void> | void
  onToggleContinuousMode: () => void
  volume: number
  onVolumeChange: (value: number) => void
}

function AudioPlayerChrome(props: AudioPlayerChromeProps) {
  if (isPrerendering()) return null
  return (
    <Box data-testid="audio-player-chrome">
      {props.active && (
        <InlinePlayer
          isPlaying={props.isPlaying}
          loading={props.loading}
          currentTime={props.currentTime}
          duration={props.duration}
          activeSurahNumber={props.surahNumber}
          currentAyahNumber={props.currentAyahNumber}
          onPlayPause={props.onPlayPause}
          onNext={props.onNext}
          onPrevious={props.onPrevious}
          onSeek={props.onSeek}
          onSeekToAyah={props.onSeekToAyah}
          continuousMode={props.continuousMode}
          onToggleContinuousMode={props.onToggleContinuousMode}
          volume={props.volume}
          onVolumeChange={props.onVolumeChange}
        />
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Continue-in-reader CTA
// ---------------------------------------------------------------------------

interface ContinueInReaderChromeProps {
  surahNumber: number
}

/**
 * The clear path from this SEO page into the full interactive Reader
 * (design item 3 of the reader-unification doc). Top-floating so it reads
 * as prominent without disturbing the SEO body's own layout — the body
 * doesn't know this exists, same boundary the other chrome wrappers keep.
 *
 * Gated by `isPrerendering()` like every other chrome wrapper here: the
 * prerendered snapshot must never carry a link to `/reader`, which
 * robots.txt Disallows (see the file doc-comment above and
 * SurahIndexList.tsx's matching rule for the crawl-hub side of the same
 * invariant). A real visitor only ever sees this after hydration.
 */
function ContinueInReaderChrome({ surahNumber }: ContinueInReaderChromeProps) {
  // Hooks BEFORE the prerender guard: hooks must run in the same order on
  // every render, and an early return above them is a rules-of-hooks
  // violation (React error #310 if the guard's value ever changes).
  const { t } = useTranslation()
  const navigate = useNavigate()
  if (isPrerendering()) return null
  return (
    <Box
      data-testid="continue-in-reader-chrome"
      sx={{
        position: 'fixed',
        top: 12,
        insetInlineStart: '50%',
        transform: 'translateX(-50%)',
        zIndex: (theme) => theme.zIndex.appBar - 1,
      }}
    >
      <Button
        variant="contained"
        size="small"
        onClick={() => navigate(`/reader/${surahNumber}`)}
      >
        {t('seo.continueInReader')}
      </Button>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Reader settings chrome
// ---------------------------------------------------------------------------

type ArabicFont = 'kfgqpc' | 'uthmani' | 'indopak' | 'simple'
type ViewMode = 'verses' | 'book'

interface ReaderSettingsChromeProps {
  edition: string
  availableEditions: Edition[]
  reciter: string
  arabicFont: ArabicFont
  fontSize: number
  wbwEnabled: boolean
  viewMode: ViewMode
  onEditionChange: (v: string) => void
  onReciterChange: (v: string) => void
  onArabicFontChange: (v: ArabicFont) => void
  onFontSizeChange: (v: number) => void
  onWbwToggle: () => void
  onViewModeChange: (m: ViewMode) => void
}

function ReaderSettingsChrome(props: ReaderSettingsChromeProps) {
  if (isPrerendering()) return null
  return (
    <Box data-testid="reader-settings-chrome">
      <ReaderFloatingSettings
        edition={props.edition}
        availableEditions={props.availableEditions}
        reciter={props.reciter}
        arabicFont={props.arabicFont}
        fontSize={props.fontSize}
        wbwEnabled={props.wbwEnabled}
        viewMode={props.viewMode}
        onEditionChange={props.onEditionChange}
        onReciterChange={props.onReciterChange}
        onArabicFontChange={props.onArabicFontChange}
        onFontSizeChange={props.onFontSizeChange}
        onWbwToggle={props.onWbwToggle}
        onViewModeChange={props.onViewModeChange}
      />
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Per-ayah action buttons
// ---------------------------------------------------------------------------
//
// The SEO body of SurahReaderPage renders one <article id="ayah-N"> per
// ayah. The chrome buttons must live INSIDE those articles so they sit
// next to the verse they act on, but we don't want the SEO body to know
// about the chrome at all — keeping that boundary clean is the whole
// point of the gating pattern.
//
// `AyahActionsChromeMount` walks the DOM after the SEO body has
// rendered, finds each `<article id="ayah-N">`, and appends a chrome
// `<div>` to each one. React then portals an `AyahActionsChrome`
// component into each div via `createPortal`.
//
// On unmount (or when the surah changes), the div is removed cleanly.

interface AyahActionsChromeMountProps {
  ayahs: Ayah[]
  isAuthenticated: boolean
  isBookmarked: (ayahNumber: number) => boolean
  isInMemorization: (ayahNumber: number) => boolean
  currentAyahIndex: number
  isPlaying: boolean
  onPlay: (index: number) => void
  onBookmarkToggle: (ayahNumber: number) => void
  onMemorizeToggle: (ayahNumber: number) => void
}

function AyahActionsChromeMount(props: AyahActionsChromeMountProps) {
  const [mountPoints, setMountPoints] = useState<Map<number, HTMLDivElement>>(new Map())

  useEffect(() => {
    if (props.ayahs.length === 0) {
      setMountPoints(new Map())
      return
    }
    const next = new Map<number, HTMLDivElement>()
    for (const ayah of props.ayahs) {
      const article = document.getElementById(`ayah-${ayah.ayahNumber}`)
      if (!article) continue
      // Reuse an existing mount div if present so React doesn't tear
      // down and re-mount on each ayahs-array re-reference.
      let div = article.querySelector<HTMLDivElement>('div[data-ayah-chrome="true"]')
      if (!div) {
        div = document.createElement('div')
        div.setAttribute('data-ayah-chrome', 'true')
        article.appendChild(div)
      }
      next.set(ayah.ayahNumber, div)
    }
    setMountPoints(next)
    return () => {
      for (const div of next.values()) {
        div.parentElement?.removeChild(div)
      }
    }
  }, [props.ayahs])

  return (
    <>
      {props.ayahs.map((ayah, index) => {
        const mount = mountPoints.get(ayah.ayahNumber)
        if (!mount) return null
        return createPortal(
          <AyahActionsChrome
            key={ayah.ayahNumber}
            ayahNumber={ayah.ayahNumber}
            isAuthenticated={props.isAuthenticated}
            isBookmarked={props.isBookmarked(ayah.ayahNumber)}
            isInMemorization={props.isInMemorization(ayah.ayahNumber)}
            isPlaying={props.isPlaying && props.currentAyahIndex === index}
            onPlay={() => props.onPlay(index)}
            onBookmarkToggle={() => props.onBookmarkToggle(ayah.ayahNumber)}
            onMemorizeToggle={() => props.onMemorizeToggle(ayah.ayahNumber)}
          />,
          mount,
        )
      })}
    </>
  )
}

interface AyahActionsChromeProps {
  ayahNumber: number
  isAuthenticated: boolean
  isBookmarked: boolean
  isInMemorization: boolean
  isPlaying: boolean
  onPlay: () => void
  onBookmarkToggle: () => void
  onMemorizeToggle: () => void
}

function AyahActionsChrome(props: AyahActionsChromeProps) {
  // Hook before the prerender guard — see ContinueInReaderChrome above.
  const { t } = useTranslation()
  if (isPrerendering()) return null
  return (
    <Stack
      data-testid={`ayah-actions-chrome-${props.ayahNumber}`}
      direction="row"
      spacing={0.5}
      sx={{ mt: 1, alignItems: 'center' }}
    >
      <Tooltip title={t('reader.playAudio')}>
        <IconButton
          size="small"
          onClick={props.onPlay}
          color={props.isPlaying ? 'primary' : 'default'}
          aria-label={t('reader.playAudio')}
        >
          <PlayArrowIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {props.isAuthenticated && (
        <>
          <Tooltip
            title={t(props.isBookmarked ? 'reader.removeBookmark' : 'reader.addBookmark')}
          >
            <IconButton
              size="small"
              onClick={props.onBookmarkToggle}
              aria-pressed={props.isBookmarked}
              aria-label={t(props.isBookmarked ? 'reader.removeBookmark' : 'reader.addBookmark')}
            >
              {props.isBookmarked ? (
                <BookmarkIcon fontSize="small" color="primary" />
              ) : (
                <BookmarkBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip
            title={t(
              props.isInMemorization
                ? 'reader.removeFromMemorization'
                : 'reader.addToMemorization',
            )}
          >
            <IconButton
              size="small"
              onClick={props.onMemorizeToggle}
              aria-pressed={props.isInMemorization}
              aria-label={t(
                props.isInMemorization
                  ? 'reader.removeFromMemorization'
                  : 'reader.addToMemorization',
              )}
            >
              {props.isInMemorization ? (
                <SchoolIcon fontSize="small" color="primary" />
              ) : (
                <SchoolOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  )
}

